/** Formatting helpers and the Boli glyph. Numbers always carry thousands separators. */
import type { ReactElement } from "react";

export const formatNumber = (n: number): string => new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(Math.trunc(n));
/**
 * D-163: a balance short enough for the app bar. In full up to 9,999; then 10k, 12.3k, 99.9k, 250k, 1.2M. Always rounded
 * down, so it never shows more than you have (99,950 is 99.9k, not 100k).
 */
export function formatCompact(n: number): string {
  const v = Math.trunc(n), a = Math.abs(v), sign = v < 0 ? "-" : "";
  const cut = (x: number, d: number): string => { const f = 10 ** d; return String(Math.floor(x * f) / f); };
  if (a < 10_000) return formatNumber(v);
  if (a < 100_000) return `${sign}${cut(a / 1000, 1)}k`;
  if (a < 1_000_000) return `${sign}${Math.floor(a / 1000)}k`;
  if (a < 100_000_000) return `${sign}${cut(a / 1_000_000, 1)}M`;
  if (a < 1_000_000_000) return `${sign}${Math.floor(a / 1_000_000)}M`;
  // D-205: never wider than five characters (12.3M), so the app bar fits at 320 px whatever the balance.
  return `${sign}${Math.min(999, Math.floor(a / 1_000_000_000))}B`;
}
export const formatBoli = (n: number): string => formatNumber(n);
export const formatSeconds = (s: number): string => `${formatNumber(s)} s`;
export const formatMvr = (minor: number): string => `MVR ${formatNumber(Math.round(minor / 100))}`;
export const plural = (n: number, one: string, many = `${one}s`): string => `${formatNumber(n)} ${n === 1 ? one : many}`;
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;

export const greeting = (d = new Date()): string => {
  const h = d.getHours();
  if (h < 12) return "Morning";
  if (h < 18) return "Afternoon";
  return "Evening";
};

const DAY = 86_400_000;
export function relativeTime(at: number, now = Date.now()): string {
  const diff = now - at;
  if (diff < 60_000) return "Just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  const d = new Date(at); const n = new Date(now);
  const sameDay = d.toDateString() === n.toDateString();
  const time = time24(at);
  if (sameDay) return `${d.getHours() >= 18 ? "Tonight" : "Today"}, ${time}`;
  if (diff < 2 * DAY && new Date(now - DAY).toDateString() === d.toDateString()) return `Yesterday, ${time}`;
  if (diff < 7 * DAY) return d.toLocaleDateString("en-GB", { weekday: "long" });
  return formatDate(at);
}
export const formatDate = (at: number): string => new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "long" });

/** Win rate, always a percentage (D-085): "38%", and "0%" before any games, never a dash (audit S4). */
export const winRate = (won: number, of: number): string => `${of > 0 ? Math.round((won / of) * 100) : 0}%`;

const fmtDate = (at: number, opts: Intl.DateTimeFormatOptions): string => new Intl.DateTimeFormat("en-GB", opts).format(new Date(at));
/** 24-hour clock everywhere: "05:09", "21:40". */
export const time24 = (at: number): string => fmtDate(at, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
/** "Tuesday 27 October, 05:09": a date in a sentence or a detail page. */
export const dateLong = (at: number): string => `${fmtDate(at, { weekday: "long", day: "numeric", month: "long" })}, ${time24(at)}`;
/** "Tue 27 Oct, 05:09": a date in a row or a tile. */
export const dateShort = (at: number): string => `${fmtDate(at, { weekday: "short", day: "numeric", month: "short" })}, ${time24(at)}`;
/** The line under a name (audit #41, FigCraft p51): "@husham · Signature member", or "@ozzie · Level 3" without one. */
export const handleLine = (username: string, tierName: string | null, level: number): string =>
  `@${username} · ${tierName ? `${tierName} member` : `Level ${formatNumber(level)}`}`;
/** A price as words, for labels and sentences: "120 Boli". On screen prices show the cowrie (`Boli`). */
export const priceBoli = (n: number): string => `${formatNumber(n)} Boli`;

/** Card names: rank + suit glyph, e.g. "7♦". */
export { face as cardName } from "@digu/shared";

/** Boli: a photographed cowrie seen from its toothed underside (D-081). Decorative; amounts carry their own label. */
export function Cowrie({ size = 14, className = "" }: { size?: number; className?: string }): ReactElement {
  return <img className={`money__glyph currency-icon ${className}`} src="/art/v5/glyphs/boli.svg" width={size} height={size} alt="" aria-hidden="true" draggable={false} />;
}

export type MoneySize = "num-xl" | "num-l" | "num-m" | "num-s" | "small-b";
const FONT_PX: Record<MoneySize, number> = { "num-xl": 72, "num-l": 36, "num-m": 22, "num-s": 16, "small-b": 13 };

/** A Boli amount: number plus the shell glyph. Negative amounts use alert-text; `tone="neutral"` uses text. */
export function Boli({ n, size = "num-s", tone, signed = false, className = "", label }: { n: number; size?: MoneySize; tone?: "accent" | "neutral" | "alert"; signed?: boolean; className?: string; label?: string }): ReactElement {
  const neg = n < 0;
  const t = tone ?? (neg ? "alert" : "accent");
  const text = `${signed && n > 0 ? "+" : neg ? "−" : ""}${formatNumber(Math.abs(n))}`;
  return (
    <span className={`money ${size} ${t === "alert" ? "money--neg" : t === "neutral" ? "money--neutral" : ""} ${className}`} aria-label={label ?? `${text} Boli`}>
      <span aria-hidden="true">{text}</span>
      <Cowrie size={Math.round(FONT_PX[size] * 1.15)} />
    </span>
  );
}
